import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Input from '../../components/input/Input'
import { Controller, useForm } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks'
import { setUser } from '../../redux/slices/UserSlice/UserSlice'
import { useNavigate } from 'react-router'
import { apiGetServerUser } from '../../api/services/getserveruser/getserveruser.service'
import { ApiGetServerUserResponse, ErrorList, ServerList } from 'src/api'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography
} from '@mui/material'
import { selectUser } from '../../redux/slices/UserSlice/UserSlice.selectors'
import { useCookies } from 'react-cookie'
import { LoadingButton } from '@mui/lab'
import useSentry from 'src/hooks/useSentry'
import PersonManagementImg from 'src/assets/images/person_management.svg'
import AvailroomWhiteLogoImg from 'src/assets/images/logo_availroom_blanco2020.svg'
import axios from 'axios'
import { toast } from 'react-toastify'

interface formValues {
  userName: string
  password: string
  rememberMe: boolean
}

const Auth = () => {
  const { t } = useTranslation()
  const { handleSubmit, control, setError } = useForm<formValues>()

  // redux hooks
  const dispatch = useAppDispatch()
  const { token: userToken } = useAppSelector(selectUser)

  const { captureError } = useSentry()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [openSelectServerModal, setOpenSelectServerModal] = useState(false)
  const [selectedEnvironment, setSelectedEnvironment] = useState('')
  const [environmentList, setEnvironmentList] = useState<ServerList[]>([])
  const [_cookie, setCookie] = useCookies(['languagecookie'])
  const [loginData, setLoginData] = useState<{
    serverUserData: ApiGetServerUserResponse
    userData: formValues
  }>()

  const makeLoginRequest = (serverUrl: string, data: formValues) => {
    return axios.put<{
      language: string
    }>(
      `https://${serverUrl}/sialdPMS/web/resourcescrossorigin/webcrossorigin/login`,
      data,
      {
        withCredentials: true
      }
    )
  }

  const loginToServer = async (
    resData: ApiGetServerUserResponse,
    data: formValues
  ) => {
    await makeLoginRequest(resData.serverUrl, data)
    const loginRes = await makeLoginRequest(resData.serverUrl, data)
    const loginResData = loginRes.data
    setCookie('languagecookie', loginResData.language, {
      sameSite: 'none',
      secure: true,
      domain: '.availroom.com',
      path: '/'
    })
    dispatch(
      setUser({
        rememberMe: data.rememberMe,
        name: resData.name,
        serverList: resData.serverList,
        serverUrl: resData.serverUrl,
        surname: resData.surname,
        token: resData.token,
        userName: data.userName
      })
    )
    navigate('/')
  }

  const onSubmit = handleSubmit(async (data) => {
    setLoading(true)
    try {
      const response = await apiGetServerUser(data)
      const resData = response
      if (!resData.ok) {
        setLoading(false)
        throw response
      }
      const isRootUser = !resData.serverUrl
      if (isRootUser) {
        setEnvironmentList(resData.serverList)
        setLoginData({
          serverUserData: resData,
          userData: data
        })
        setLoading(false)
        return setOpenSelectServerModal(true)
      }
      await loginToServer(resData, data)
    } catch (error: any) {
      console.log(error)
      setLoading(false)
      if ('errorList' in error) {
        const errorList = error.errorList as ErrorList[]
        errorList.forEach(({ field, error }) => {
          if (field === 'userName') {
            return setError('userName', {
              message: error,
              type: 'validate'
            })
          }
          if (field === 'password') {
            console.log('here')
            return setError('password', {
              message: error,
              type: 'validate'
            })
          }
        })
        return
      }
      toast.error(t('alerts.An_error_has_ocurred_try_again'))
      captureError(error, {
        description: 'error from handleOnSubmit Auth.tsx'
      })
    }
  })

  const handleSubmitEnvironment = async () => {
    try {
      setLoading(true)
      const response = await apiGetServerUser({
        ...loginData!.userData,
        serverPath: selectedEnvironment
      })
      await loginToServer(response, loginData!.userData)
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (userToken) {
      navigate('/')
    }
  }, [])

  return (
    <Box>
      <Box
        sx={{
          display: 'flex'
        }}
      >
        <Box
          sx={{
            height: '100vh',
            width: '50%',
            backgroundColor: '#2C2CE7',
            display: 'grid',
            placeItems: 'center',
            position: 'relative'
          }}
        >
          <Box
            component={'img'}
            src={AvailroomWhiteLogoImg}
            sx={{
              position: 'absolute',
              left: '2rem',
              top: '2rem',
              width: '30%',
              maxWidth: '250px'
            }}
          />
          <Box
            component={'img'}
            src={PersonManagementImg}
            sx={{
              width: '70%',
              maxWidth: 700
            }}
          />
        </Box>

        <Box
          component={'form'}
          onSubmit={onSubmit}
          sx={{
            height: '100vh',
            width: '50%',
            display: 'grid',
            placeItems: 'center',
            position: 'relative'
          }}
        >
          <Box
            sx={{
              width: '70%',
              maxWidth: 500
            }}
          >
            <Typography
              variant='h6'
              sx={{
                color: ({ palette }) => palette.text.primaryLight
              }}
            >
              {t('texts.Welcome_to_AvailRoom')}
            </Typography>
            <Box
              sx={{
                marginTop: '3rem'
              }}
            >
              <Controller
                control={control}
                name='userName'
                rules={{
                  required: {
                    message: t('validations.required'),
                    value: true
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label={t('form.labels.User(email)')}
                    error={!!error}
                    helperText={error?.message}
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name='password'
                rules={{
                  required: {
                    message: t('validations.required'),
                    value: true
                  }
                }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    sx={{ marginTop: '1rem' }}
                    type='password'
                    label={t('form.labels.Password')}
                    error={!!error}
                    helperText={error?.message}
                    {...field}
                  />
                )}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                marginTop: '1rem'
              }}
            >
              <Controller
                control={control}
                name='rememberMe'
                render={({ field: { value, onChange, ...field } }) => (
                  <FormControlLabel
                    checked={value}
                    {...field}
                    control={
                      <Checkbox
                        onChange={(e) => {
                          onChange(e.target.checked)
                        }}
                      />
                    }
                    label={t('labels.Remember_my_data')}
                  />
                )}
              />
            </Box>
            <LoadingButton
              loading={loading}
              fullWidth
              variant='contained'
              size='large'
              type='submit'
              sx={{
                marginTop: '1rem'
              }}
            >
              {t('buttons.Access')}
            </LoadingButton>
          </Box>
        </Box>
      </Box>
      {/* EXPECTING TO BE CHANGED */}
      <Modal open={openSelectServerModal}>
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            backgroundColor: 'white',
            boxShadow: 3,
            transform: 'translate(-50%, -50%)',
            padding: '2rem',
            width: '400px',
            height: 300
          }}
        >
          <Typography>{t('labels.Select_Environment')}</Typography>
          <FormControl
            fullWidth
            sx={{
              marginTop: '1rem'
            }}
          >
            <InputLabel id='demo-simple-select-label'>
              {t('labels.Environment')}
            </InputLabel>
            <Select
              labelId='demo-simple-select-label'
              id='demo-simple-select'
              value={selectedEnvironment}
              label={t('labels.Environment')}
              onChange={(e) => {
                setSelectedEnvironment(e.target.value!)
              }}
            >
              {environmentList.map((environment) => (
                <MenuItem
                  value={environment.serverPath}
                  key={environment.serverPath}
                >
                  {environment.serverPath}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box
            sx={{
              marginTop: '1rem'
            }}
          >
            <LoadingButton
              loading={loading}
              variant='contained'
              onClick={handleSubmitEnvironment}
            >
              {t('buttons.Accept')}
            </LoadingButton>
          </Box>
        </Box>
      </Modal>
    </Box>
  )
}

export default Auth
